import { useEffect, useRef, useState } from "react";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { CheckIcon, MapPinIcon, PencilIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getOrderById } from "../../api/orderService";
import {
  parseTrackingNumber,
  updateShippingStatus,
} from "../../api/statusService";
import ParseFailedModal from "../../components/modals/ParseFailedModal";
import TrackingConfirmModal from "../../components/modals/TrackingConfirmModal";
import DividerWithText from "../../components/dividerWithText";
import LoadingScreen from "../../components/loadingScreen";
import PhotoCaptureBox from "../../components/photoCaptureBox";
import { Controller, useForm, useWatch } from "react-hook-form";
import { ANALYTICS_EVENTS, trackEvent } from "../../lib/analytics";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import clsx from "clsx";
import { carrierOptions } from "../../constants";

type ShippingStatusFormValues = {
  trackingInfo: { carrier: string };
};

export default function ShippingStatusPage() {
  const { control, handleSubmit, setValue } = useForm<ShippingStatusFormValues>(
    {
      defaultValues: {
        trackingInfo: { carrier: "" },
      },
    },
  );

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("id");

  const [showTracking, setShowTracking] = useState(false);
  const [trackingId, setTrackingId] = useState("");
  const [prevTrackingId, setPrevTrackingId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [parsedData, setParsedData] = useState<string | null>(null);
  const [trackingImageFile, setTrackingImageFile] = useState<File | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [trackingManualEntryTracked, setTrackingManualEntryTracked] =
    useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const selectedCarrier = useWatch({
    control,
    name: "trackingInfo.carrier",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => getOrderById(orderId!),
    enabled: !!orderId,
  });

  const clearLocalTrackingImage = () => {
    setTrackingImageFile(null);
  };

  const resetTrackingPhoto = () => {
    setTrackingImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (prevTrackingId !== null) {
      setTrackingId(prevTrackingId);
    }
  };

  const updateMutation = useMutation({
    mutationFn: async ({
      trackingInfo,
      trackingImage,
    }: {
      trackingInfo: { trackingNumber: string; carrier: string };
      trackingImage: File | null;
    }) => {
      return updateShippingStatus(orderId!, trackingInfo, trackingImage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      setIsEditing(false);
      setShowTracking(false);
      clearLocalTrackingImage();
    },
  });

  const parseMutation = useMutation({
    mutationFn: (file: File) => {
      if (!orderId) {
        return Promise.reject(new Error("Missing orderId"));
      }
      return parseTrackingNumber(file, orderId);
    },
    onSuccess: (res) => {
      if (res?.carrier && res.carrier.toUpperCase() !== "UNKNOWN") {
        setValue("trackingInfo.carrier", res.carrier);
      } else {
        setValue("trackingInfo.carrier", "");
      }
      const extractedId = res?.trackingNumber;
      if (
        extractedId &&
        extractedId.toUpperCase() !== "UNKNOWN" &&
        extractedId.length < 30
      ) {
        setParsedData(extractedId);
        setShowConfirmModal(true);
        trackEvent(ANALYTICS_EVENTS.TRACKING_PARSE_SUCCESS);
      } else {
        setShowFailedModal(true);
        trackEvent(ANALYTICS_EVENTS.TRACKING_PARSE_FAILED, {
          reason: "invalid_result",
        });
      }
    },
    onError: () => {
      setShowFailedModal(true);
      trackEvent(ANALYTICS_EVENTS.TRACKING_PARSE_FAILED, {
        reason: "api_error",
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPrevTrackingId(trackingId);
      setTrackingImageFile(file);
      trackEvent(ANALYTICS_EVENTS.TRACKING_PHOTO_TAKEN);
      parseMutation.mutate(file);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleConfirm = () => {
    if (parsedData) setTrackingId(parsedData);
    setShowConfirmModal(false);
    setParsedData(null);
  };

  const reopenTrackingPicker = () => {
    const input = fileInputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!input) return;

    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }

    input.click();
  };

  const onSubmit = (formData: ShippingStatusFormValues) => {
    trackEvent(ANALYTICS_EVENTS.TRACKING_SUBMITTED);
    updateMutation.mutate({
      trackingInfo: {
        trackingNumber: trackingId,
        carrier: formData.trackingInfo.carrier,
      },
      trackingImage: trackingImageFile,
    });
  };

  useEffect(() => {
    if (isEditing && editInputRef.current) editInputRef.current.focus();
  }, [isEditing]);

  if (isLoading) return <LoadingScreen />;

  if (!data) return null;

  return (
    <div
      className={clsx(
        "min-h-screen bg-[#F9FAFB] flex flex-col md:p-6 p-4 font-sans",
        parseMutation.isPending && "pointer-events-none select-none opacity-50",
      )}
    >
      <input
        type="file"
        id="trackingCameraInput"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="absolute h-0 w-0 opacity-0 -z-10"
      />

      <div className="w-full max-w-xl md:ml-28 mt-9 mb-12">
        <button
          onClick={() =>
            navigate(`/order?id=${orderId}`, { state: { fromStatus: true } })
          }
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors font-medium cursor-pointer"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to order details
        </button>
      </div>

      {!data.trackingInfo?.trackingNumber ? (
        <div className="w-full max-w-xl mx-auto space-y-6">
          <div className="bg-white rounded-xl md:px-10 px-4 py-7 shadow-sm border border-slate-100 flex flex-col items-center">
            <h2 className="md:text-2xl text-lg font-medium text-slate-800 mb-6 text-center">
              Send your item to our facility at:
            </h2>
            <div className="w-full bg-blue-50 border border-[#D1E4FF] rounded-xl md:px-5 md:py-4 px-4 py-3 flex items-center gap-4">
              <MapPinIcon className="w-6 h-6 text-[#3366CC] shrink-0" />
              <div className="flex flex-col gap-0.5 w-full">
                <p className="text-[#3366CC] md:text-lg text-sm font-medium leading-relaxed">
                  360 Parts LLC
                </p>
                <p className="text-[#3366CC] md:text-lg text-sm leading-relaxed">
                  14613 Ballyclarc dr, Austin, TX 78717, United States
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl md:px-10 px-4 py-7 shadow-sm border border-slate-100">
            <h2 className="md:text-2xl text-lg font-medium text-slate-800 mb-8 text-center mx-auto">
              Already shipped your item?
            </h2>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mb-6">
              <button
                onClick={() => setShowTracking(true)}
                className={`w-full py-6 px-4 border rounded-xl font-medium transition-all text-center cursor-pointer ${showTracking ? "bg-blue-50 border-blue-200 text-blue-700 shadow-sm" : "border-gray-300 text-slate-700 hover:bg-slate-50"}`}
              >
                Yes, I've shipped it
              </button>
              <button
                onClick={() => {
                  setShowTracking(false);
                  setTrackingId("");
                  clearLocalTrackingImage();
                  setValue("trackingInfo.carrier", "");
                }}
                className={`w-full py-6 px-4 border rounded-xl font-medium transition-all text-center cursor-pointer ${!showTracking ? "bg-blue-50 border-blue-200 text-blue-700 shadow-sm" : "border-gray-300 text-slate-700 hover:bg-slate-50"}`}
              >
                Not yet
              </button>
            </div>

            {showTracking && (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                <PhotoCaptureBox
                  inputId="trackingCameraInput"
                  isParsing={parseMutation.isPending}
                  hasCapturedPhoto={!!trackingImageFile}
                  idleText="Take a picture of the tracking label or receipt"
                  parsingText="Scanning image..."
                  onReset={resetTrackingPhoto}
                />

                <DividerWithText text="No label or receipt? Enter the tracking number manually" />

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-slate-600">
                    Tracking ID
                  </label>
                  <input
                    type="text"
                    value={trackingId}
                    onChange={(e) => {
                      setTrackingId(e.target.value);
                      if (!trackingManualEntryTracked && e.target.value) {
                        trackEvent(ANALYTICS_EVENTS.TRACKING_MANUAL_ENTRY);
                        setTrackingManualEntryTracked(true);
                      }
                    }}
                    placeholder="Enter your tracking number"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <Controller
                  name="trackingInfo.carrier"
                  control={control}
                  rules={{ required: "Carrier is required" }}
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-slate-600">
                        Carrier
                      </label>

                      <Listbox value={field.value} onChange={field.onChange}>
                        <div className="relative">
                          <ListboxButton className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                            <span
                              className={clsx(
                                "truncate",
                                !field.value && "text-gray-400",
                              )}
                            >
                              {field.value || "Select carrier"}
                            </span>
                            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                          </ListboxButton>
                          <ListboxOptions
                            anchor="bottom"
                            className="mt-1 w-(--button-width) rounded-xl border border-gray-200 bg-white p-1 shadow-lg outline-none z-20"
                          >
                            {carrierOptions.map((carrier) => (
                              <ListboxOption
                                key={carrier}
                                value={carrier}
                                className="cursor-pointer select-none rounded-lg px-3 py-2 text-sm data-focus:bg-blue-50 data-focus:text-blue-500"
                              >
                                {carrier}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </div>
                      </Listbox>
                    </div>
                  )}
                />

                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    !trackingId || !selectedCarrier || updateMutation.isPending
                  }
                  className={`w-full py-4 rounded-xl font-semibold transition-colors shadow-md shadow-blue-200 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 cursor-pointer`}
                >
                  {updateMutation.isPending ? "Confirming..." : "Confirm"}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl md:px-10 px-6 py-7 shadow-sm border border-slate-100 flex flex-col items-center w-full max-w-xl mx-auto">
          <h2 className="md:text-2xl text-xl font-medium text-slate-900 mb-8 text-center">
            Shipping details
          </h2>
          <div className="w-full space-y-4">
            <div className="bg-[#FBFDFF] border border-slate-100 rounded-2xl md:p-5 px-4 py-3 flex gap-4">
              <MapPinIcon className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <h3 className="text-blue-500 md:text-xl text-sm font-medium">
                  Your item has been sent to our facility at:
                </h3>
                <p className="text-slate-500 md:text-xl text-xs font-medium leading-snug">
                  360 Parts LLC
                </p>
                <p className="text-slate-500 md:text-xl text-xs font-normal leading-snug">
                  14613 Ballyclarc dr, Austin, TX 78717, United States
                </p>
              </div>
            </div>

            <div className="bg-[#FBFDFF] border border-slate-100 rounded-2xl md:p-5 px-4 py-3 flex gap-4">
              <CheckIcon className="w-6 h-6 text-blue-500 shrink-0 mt-0.5 font-bold" />
              <div className="w-full flex flex-col gap-1">
                <h3 className="text-blue-500 md:text-xl text-sm font-medium mb-3">
                  Shipment confirmed
                </h3>

                {!isEditing ? (
                  <div className="flex flex-col gap-4">
                    <div>
                      <p className="text-slate-400 md:text-xl text-xs tracking-wider font-normal mb-1">
                        Tracking ID
                      </p>
                      <div className="flex items-center">
                        <span className="font-normal text-slate-600 md:text-xl text-xs break-all">
                          {data.trackingInfo?.trackingNumber}
                        </span>
                        <button
                          onClick={() => {
                            setValue(
                              "trackingInfo.carrier",
                              data.trackingInfo?.carrier,
                            );
                            setIsEditing(true);
                          }}
                          className="text-slate-600 hover:text-slate-500 md:ml-4 ml-2 cursor-pointer transition-colors"
                        >
                          <PencilIcon className="md:w-5 md:h-5 w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="text-slate-400 md:text-xl text-xs tracking-wider font-normal">
                        Carrier
                      </p>
                      <span className="font-normal text-slate-600 md:text-xl text-xs">
                        {data.trackingInfo?.carrier}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 mt-2 w-full animate-in fade-in">
                    <div className="flex flex-col gap-1">
                      <label className="text-slate-400 md:text-xl text-xs tracking-wider font-normal">
                        Tracking ID
                      </label>
                      <input
                        ref={editInputRef}
                        type="text"
                        defaultValue={data.trackingInfo?.trackingNumber}
                        className="w-full font-normal text-slate-600 md:text-lg text-sm border border-blue-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-slate-400 md:text-xl text-xs tracking-wider font-normal">
                        Carrier
                      </label>
                      <Controller
                        name="trackingInfo.carrier"
                        control={control}
                        render={({ field }) => (
                          <Listbox
                            value={field.value}
                            onChange={field.onChange}
                          >
                            <div className="relative">
                              <ListboxButton className="flex w-full items-center justify-between border border-blue-200 rounded-lg px-3 py-2 bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <span className="font-normal text-slate-600 md:text-lg text-sm truncate">
                                  {field.value || data.trackingInfo?.carrier}
                                </span>
                                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                              </ListboxButton>
                              <ListboxOptions
                                anchor="bottom"
                                className="mt-1 w-(--button-width) rounded-xl border border-gray-200 bg-white p-1 shadow-lg outline-none z-20"
                              >
                                {carrierOptions.map((carrier) => (
                                  <ListboxOption
                                    key={carrier}
                                    value={carrier}
                                    className="cursor-pointer select-none rounded-lg px-3 py-2 text-sm data-focus:bg-blue-50 data-focus:text-blue-500"
                                  >
                                    {carrier}
                                  </ListboxOption>
                                ))}
                              </ListboxOptions>
                            </div>
                          </Listbox>
                        )}
                      />
                    </div>

                    <div className="flex gap-2 w-full mt-2">
                      <button
                        onClick={() => setIsEditing(false)}
                        className="flex-1 bg-white border border-slate-200 text-slate-700 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          const trackingNumberValue =
                            editInputRef.current?.value.trim();
                          if (!trackingNumberValue) {
                            return;
                          }
                          trackEvent(
                            ANALYTICS_EVENTS.TRACKING_STATUS_UPDATE_CLICKED,
                          );
                          updateMutation.mutate({
                            trackingInfo: {
                              trackingNumber: trackingNumberValue,
                              carrier: selectedCarrier,
                            },
                            trackingImage: null,
                          });
                        }}
                        disabled={updateMutation.isPending}
                        className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors shadow-sm cursor-pointer"
                      >
                        {updateMutation.isPending ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <TrackingConfirmModal
        isOpen={showConfirmModal}
        parsedData={parsedData}
        onClose={() => {
          setShowConfirmModal(false);
          clearLocalTrackingImage();
        }}
        onConfirm={handleConfirm}
      />
      <ParseFailedModal
        isOpen={showFailedModal}
        onClose={() => {
          setShowFailedModal(false);
          clearLocalTrackingImage();
        }}
        onTryAgain={() => {
          setShowFailedModal(false);
          reopenTrackingPicker();
        }}
      />
    </div>
  );
}
